import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { format, formatDistanceToNow, addDays, addWeeks, addMonths, addYears, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  Star,
  Grid3X3,
  List,
  Mail,
  MessageSquare,
  CheckSquare,
  CheckSquare2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  BarChart3,
  Folder,
  Clock,
  GitBranch,
  FileCode,
  Database,
  File,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  X,
  Copy,
  Trash2,
  Pencil,
  Lock,
  Globe,
  FolderInput,
  Play,
  Eye,
  CheckCircle2,
  Circle,
  XCircle,
  Calendar,
  CalendarDays,
  CalendarRange,
  Webhook,
  Filter,
  Users,
  ListChecks,
  Flag,
  Minus,
  ChevronsUp,
  AlertTriangle,
  FileCheck
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { CreateTaskSheet } from './CreateTaskSheet';
import { ViewTaskSheet } from './ViewTaskSheet';
import { EditTaskSheet } from './EditTaskSheet';
import { CreateWorkflowModal } from './CreateWorkflowModal';
import { toast } from 'sonner';
import { TasksSidebar } from '@/components/TasksSidebar';

// Priority configuration for visual indicators
const PRIORITY_CONFIG = {
  low: { label: 'Low', icon: Minus, color: 'text-gray-500', bg: 'bg-gray-100' },
  medium: { label: 'Medium', icon: Flag, color: 'text-blue-500', bg: 'bg-blue-100' },
  high: { label: 'High', icon: ChevronUp, color: 'text-orange-500', bg: 'bg-orange-100' },
  urgent: { label: 'Urgent', icon: ChevronsUp, color: 'text-red-500', bg: 'bg-red-100' },
};

export default function TasksPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile, company } = useAuth();
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle(space?.name ? `${space.name} Tasks` : 'Tasks');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);
  // View state - 'tasks', 'library', 'live-runs', 'schedules', or 'outstanding'
  const [currentView, setCurrentViewState] = useState(() => {
    const view = searchParams.get('view');
    if (['library', 'live-runs', 'schedules', 'outstanding'].includes(view)) {
      return view;
    }
    // Legacy support: map old 'tab' param to new 'view'
    const tab = searchParams.get('tab');
    if (tab === 'library') return 'library';
    if (tab === 'reports' || tab === 'live-runs') return 'live-runs';
    return 'tasks';
  });

  // Library sub-view state - 'company', 'space', or 'personal'
  const [librarySubView, setLibrarySubViewState] = useState(() => {
    const subview = searchParams.get('subview');
    if (['company', 'space', 'personal'].includes(subview)) {
      return subview;
    }
    // Legacy support: map old 'folder' param to new 'subview'
    const folder = searchParams.get('folder');
    if (folder === 'processes') return 'company';
    if (folder === 'space') return 'space';
    return 'space'; // Default to space library
  });

  // Navigation handler - updates both state and URL
  const handleNavigate = (view, subView = null) => {
    setCurrentViewState(view);
    if (view === 'library' && subView) {
      setLibrarySubViewState(subView);
    }

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      // Remove legacy params
      newParams.delete('tab');
      newParams.delete('folder');

      if (view === 'tasks') {
        newParams.delete('view');
        newParams.delete('subview');
      } else {
        newParams.set('view', view);
        if (view === 'library' && subView) {
          newParams.set('subview', subView);
        } else {
          newParams.delete('subview');
        }
      }
      return newParams;
    }, { replace: true });
  };

  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date()); // Current month for calendar view
  const [searchQuery, setSearchQuery] = useState('');
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [createWorkflowOpen, setCreateWorkflowOpen] = useState(false);
  const [viewTaskOpen, setViewTaskOpen] = useState(false);
  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [workflowTaskRuns, setWorkflowTaskRuns] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [pages, setPages] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingWorkflowTaskRuns, setLoadingWorkflowTaskRuns] = useState(true);
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);
  const [loadingPages, setLoadingPages] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterType, setFilterType] = useState('all'); // 'all', 'workflows', 'tasks'
  const [filterAssignee, setFilterAssignee] = useState('me'); // 'me', 'anyone', 'unassigned'
  const [filterWorkflow, setFilterWorkflow] = useState('all');
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'workflow', 'assignee', 'due_date'
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [selectedTasks, setSelectedTasks] = useState(new Set());

  // Reports-specific state
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [showCompletedRuns, setShowCompletedRuns] = useState(false);
  const [selectedReportWorkflow, setSelectedReportWorkflow] = useState('all');
  const [deleteRunId, setDeleteRunId] = useState(null);

  // Enhanced Live Runs filters
  const [selectedRunStatus, setSelectedRunStatus] = useState('active');
  const [runDateRange, setRunDateRange] = useState({ from: null, to: null });
  const [tempDateRange, setTempDateRange] = useState({ from: null, to: null });
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [runSearchQuery, setRunSearchQuery] = useState('');

  // Company processes state
  const [companyProcesses, setCompanyProcesses] = useState([]);
  const [loadingCompanyProcesses, setLoadingCompanyProcesses] = useState(false);
  const [startingProcess, setStartingProcess] = useState(null);

  // Outstanding checklists state (internal_checklist requirements that are active/pending without linked task/workflow)
  const [outstandingChecklists, setOutstandingChecklists] = useState([]);
  const [loadingOutstandingChecklists, setLoadingOutstandingChecklists] = useState(false);
  const [linkedComplianceRequirement, setLinkedComplianceRequirement] = useState(null);
  const [outstandingCount, setOutstandingCount] = useState(0); // For sidebar badge (loaded eagerly)
  const [outstandingDetailsLoaded, setOutstandingDetailsLoaded] = useState(false); // Track if details are loaded

  // Site Requirements state (internal_checklist requirements that have a linked task/workflow)
  const [siteRequirements, setSiteRequirements] = useState([]);
  const [loadingSiteRequirements, setLoadingSiteRequirements] = useState(false);

  // Initial data fetch - Tasks view is default, so load tasks + outstanding count in one RPC call
  useEffect(() => {
    if (projectId && userProfile) {
      // Fetch tasks + outstanding count (single optimized RPC call)
      fetchInitialData();
      // Workflow task runs for tasks view
      if (currentView === 'tasks') {
        fetchWorkflowTaskRuns();
      }
    }
  }, [projectId, userProfile]);

  // Fetch workflow task runs when switching to tasks view
  useEffect(() => {
    if (projectId && userProfile && currentView === 'tasks' && workflowTaskRuns.length === 0) {
      fetchWorkflowTaskRuns();
    }
  }, [currentView]);

  // Fetch library data only when Library view is active (single optimized RPC call)
  useEffect(() => {
    if (projectId && userProfile && currentView === 'library') {
      fetchLibraryData(); // Single RPC replaces fetchWorkflows + fetchPages
      // Fetch company processes only when in company sub-view
      if (librarySubView === 'company') {
        fetchCompanyProcesses();
      }
    }
  }, [projectId, userProfile, currentView, librarySubView]);

  // Fetch schedules when Schedules view is active
  useEffect(() => {
    if (projectId && userProfile && currentView === 'schedules') {
      fetchSchedules();
    }
  }, [projectId, userProfile, currentView]);

  // Lazy load outstanding details when Outstanding view is active
  useEffect(() => {
    if (projectId && userProfile && currentView === 'outstanding') {
      fetchOutstandingDetails();
    }
  }, [projectId, userProfile, currentView]);

  // Fetch reports data only when Live Runs view is active
  useEffect(() => {
    if (projectId && userProfile && currentView === 'live-runs') {
      fetchWorkflowRuns();
    }
  }, [projectId, userProfile, currentView]);

  // Real-time subscription for workflow runs - only when Tasks view is active
  useEffect(() => {
    if (!projectId || currentView !== 'tasks') return;

    const channel = supabase
      .channel(`tasks-page-runs-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'checklists_workflow_runs',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[TasksPage] Workflow run change:', payload.eventType);
          // Refetch workflow task runs when runs change
          fetchWorkflowTaskRuns();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'checklists_workflow_task_runs',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[TasksPage] Task run change:', payload.eventType);
          // Refetch when task runs change
          fetchWorkflowTaskRuns();
        }
      )
      .subscribe((status, err) => {
        console.log('[TasksPage] Subscription status:', status);
        if (err) console.error('[TasksPage] Subscription error:', err);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, currentView]);

  // Combine regular tasks and workflow task runs
  const allTasks = useMemo(() => {
    return [...tasks, ...workflowTaskRuns];
  }, [tasks, workflowTaskRuns]);

  // Get unique workflows from tasks for filtering
  const uniqueWorkflows = useMemo(() => {
    const workflowMap = new Map();
    allTasks.forEach(task => {
      if (task.workflow_id && task.workflow_name) {
        workflowMap.set(task.workflow_id, task.workflow_name);
      }
    });
    return Array.from(workflowMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allTasks]);

  // Apply filters to tasks
  const filteredTasks = useMemo(() => {
    if (!allTasks.length) return [];

    let filtered = [...allTasks];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task =>
        task.name?.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query)
      );
    }

    // Type filter
    if (filterType === 'workflows') {
      filtered = filtered.filter(task => task.isWorkflowTask || task.link_to_workflow === true);
    } else if (filterType === 'tasks') {
      filtered = filtered.filter(task => !task.isWorkflowTask && !task.link_to_workflow);
    }

    // Assignee filter
    if (filterAssignee === 'me') {
      filtered = filtered.filter(task => {
        // Workflow tasks are already filtered to current user
        if (task.isWorkflowTask) return true;
        return Array.isArray(task.assigned_to) && task.assigned_to.includes(userProfile?.id);
      });
    } else if (filterAssignee === 'unassigned') {
      filtered = filtered.filter(task => {
        if (task.isWorkflowTask) return false; // Workflow tasks are assigned
        return !task.assigned_to || task.assigned_to.length === 0;
      });
    }
    // 'anyone' shows all tasks, no filter needed

    // Workflow filter
    if (filterWorkflow !== 'all') {
      filtered = filtered.filter(task => task.workflow_id === filterWorkflow);
    }

    // Completed tasks filter
    if (!showCompletedTasks) {
      filtered = filtered.filter(task => !task.myCompletion);
    }

    return filtered;
  }, [allTasks, searchQuery, filterType, filterAssignee, filterWorkflow, showCompletedTasks, userProfile?.id]);

  // Sort tasks in memory without refetching
  const sortedTasks = useMemo(() => {
    if (!filteredTasks.length) return [];

    const sorted = [...filteredTasks];

    switch (sortColumn) {
      case 'name':
        sorted.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return sortDirection === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });
        break;
      case 'due_date':
        sorted.sort((a, b) => {
          const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
          const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        });
        break;
      case 'commentCount':
        sorted.sort((a, b) => {
          const diff = (a.commentCount || 0) - (b.commentCount || 0);
          return sortDirection === 'asc' ? diff : -diff;
        });
        break;
      case 'started_at':
        sorted.sort((a, b) => {
          const dateA = a.started_at ? new Date(a.started_at).getTime() : 0;
          const dateB = b.started_at ? new Date(b.started_at).getTime() : 0;
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        });
        break;
      case 'workflow_run_name':
        sorted.sort((a, b) => {
          const nameA = (a.workflow_run_name || '').toLowerCase();
          const nameB = (b.workflow_run_name || '').toLowerCase();
          if (!nameA && !nameB) return 0;
          if (!nameA) return sortDirection === 'asc' ? 1 : -1;
          if (!nameB) return sortDirection === 'asc' ? -1 : 1;
          return sortDirection === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });
        break;
      case 'workflow_name':
        sorted.sort((a, b) => {
          const nameA = (a.workflow_name || '').toLowerCase();
          const nameB = (b.workflow_name || '').toLowerCase();
          if (!nameA && !nameB) return 0;
          if (!nameA) return sortDirection === 'asc' ? 1 : -1;
          if (!nameB) return sortDirection === 'asc' ? -1 : 1;
          return sortDirection === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });
        break;
      case 'assignees':
        sorted.sort((a, b) => {
          const countA = a.assignees?.length || 0;
          const countB = b.assignees?.length || 0;
          if (countA !== countB) {
            return sortDirection === 'asc' ? countA - countB : countB - countA;
          }
          // If same count, sort by first assignee name
          const nameA = a.assignees?.[0]?.first_name?.toLowerCase() || '';
          const nameB = b.assignees?.[0]?.first_name?.toLowerCase() || '';
          return sortDirection === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });
        break;
      case 'created_at':
      default:
        sorted.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        });
    }

    return sorted;
  }, [filteredTasks, sortColumn, sortDirection]);

  // Reports: Calculate stats across all workflows
  const reportStats = useMemo(() => {
    const activeRuns = workflowRuns.filter(r => r.status === 'in_progress').length;
    const completedRuns = workflowRuns.filter(r => r.status === 'completed').length;
    const totalTasks = workflows.reduce((sum, w) => sum + (w.tasks?.length || 0), 0);

    const assigneeIds = new Set();
    workflowRuns.forEach(run => {
      if (run.status === 'in_progress') {
        run.task_runs?.forEach(tr => {
          tr.assignees?.forEach(a => {
            // Handle different assignee formats: plain UUID string, {id: ...}, {user_id: ...}, or {type: 'user', user_id: ...}
            if (typeof a === 'string') {
              assigneeIds.add(a);
            } else {
              const id = a.id || a.user_id;
              if (id && a.type === 'user') {
                assigneeIds.add(id);
              }
            }
          });
        });
      }
    });

    return {
      activeRuns,
      completedRuns,
      totalWorkflows: workflows.length,
      totalRuns: workflowRuns.length,
      totalTasks,
      activeAssignees: assigneeIds.size,
    };
  }, [workflows, workflowRuns]);

  // Tasks: Calculate stats for the tasks view
  const taskStats = useMemo(() => {
    // Combine tasks and workflow tasks
    const allTasks = [...tasks, ...workflowTaskRuns];

    const pendingTasks = allTasks.filter(t => !t.myCompletion && t.status !== 'completed').length;
    const completedTasks = allTasks.filter(t => t.myCompletion || t.status === 'completed').length;
    const workflowTasks = allTasks.filter(t => t.isWorkflowTask).length;
    const standaloneTasks = allTasks.filter(t => !t.isWorkflowTask).length;

    // Count overdue tasks
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overdueTasks = allTasks.filter(t => {
      if (t.myCompletion || t.status === 'completed') return false;
      if (!t.due_date) return false;
      const dueDate = new Date(t.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    }).length;

    // Count unique assignees
    const assigneeIds = new Set();
    allTasks.forEach(task => {
      task.assignees?.forEach(a => {
        const id = a.id || a.user_id;
        if (id) assigneeIds.add(id);
      });
    });

    return {
      totalTasks: allTasks.length,
      pendingTasks,
      completedTasks,
      workflowTasks,
      standaloneTasks,
      overdueTasks,
      activeAssignees: assigneeIds.size,
    };
  }, [tasks, workflowTaskRuns]);

  // Reports: Filter runs based on toggles and workflow selection
  const filteredRuns = useMemo(() => {
    let runs = workflowRuns;

    if (selectedReportWorkflow !== 'all') {
      runs = runs.filter(r => r.workflow_id === selectedReportWorkflow);
    }

    if (!showCompletedRuns) {
      runs = runs.filter(r => r.status !== 'completed');
    }

    return runs;
  }, [workflowRuns, selectedReportWorkflow, showCompletedRuns]);

  // Reports: Count completed for display
  const completedRunsCount = useMemo(() => {
    if (selectedReportWorkflow === 'all') {
      return workflowRuns.filter(r => r.status === 'completed').length;
    }
    return workflowRuns.filter(r => r.workflow_id === selectedReportWorkflow && r.status === 'completed').length;
  }, [workflowRuns, selectedReportWorkflow]);

  // Reports helper functions
  const getRunDueDate = (run) => {
    const taskRuns = run.task_runs || [];
    const dueDates = taskRuns
      .filter(t => t.due_date && t.status !== 'completed')
      .map(t => new Date(t.due_date));

    if (dueDates.length === 0) return null;
    return new Date(Math.min(...dueDates));
  };

  const isDueDateOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Clock className="w-3 h-3 mr-1" /> In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline"><Circle className="w-3 h-3 mr-1" /> {status}</Badge>;
    }
  };

  const getRunProgress = (run) => {
    const taskRuns = run.task_runs || [];
    const completed = taskRuns.filter(t => t.status === 'completed').length;
    const total = taskRuns.length;
    return { completed, total, percent: total > 0 ? (completed / total) * 100 : 0 };
  };

  // ============================================================================
  // DATA FETCHING - Optimized RPCs with lazy loading per tab
  // Steve Jobs: "Simplicity is the ultimate sophistication"
  // Single RPC calls replace multiple sequential queries for each view
  // ============================================================================

  // Fetch initial data for Tasks view (default tab) - combines tasks + outstanding count
  // Replaces 8 sequential queries with 1 RPC call
  const fetchInitialData = async () => {
    try {
      setLoadingTasks(true);
      console.log('[TasksPage] Fetching initial data via RPC for project:', projectId);

      const { data, error } = await supabase.rpc('get_tasks_page_initial_data', {
        p_project_id: projectId,
        p_user_id: userProfile?.id || null
      });

      if (error) throw error;

      const {
        tasks: tasksData,
        assignee_profiles: assigneeProfiles,
        completions,
        comment_counts: commentCounts,
        outstanding_count: outstandingCountData
      } = data || {};

      // Assemble tasks with context using O(1) lookups
      const tasksWithContext = (tasksData || []).map(task => ({
        ...task,
        assignees: (task.assigned_to || [])
          .map(id => assigneeProfiles?.[id])
          .filter(Boolean),
        myCompletion: completions?.[task.id] || false,
        commentCount: commentCounts?.[task.id] || 0
      }));

      setTasks(tasksWithContext);
      setOutstandingCount(outstandingCountData || 0);
      console.log('[TasksPage] Initial data loaded:', {
        tasks: tasksWithContext.length,
        outstandingCount: outstandingCountData
      });

    } catch (error) {
      console.error('[TasksPage] Error fetching initial data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoadingTasks(false);
    }
  };

  // Legacy function for refreshing just tasks (used after create/update/delete)
  const fetchTasks = async () => {
    await fetchInitialData();
  };

  // Note: Outstanding count is now fetched as part of fetchInitialData RPC

  // Lazy load outstanding details when Outstanding tab is viewed
  // Replaces 6+ sequential queries with 1 RPC call
  const fetchOutstandingDetails = async () => {
    if (outstandingDetailsLoaded) return; // Already loaded

    try {
      setLoadingOutstandingChecklists(true);
      setLoadingSiteRequirements(true);
      console.log('[TasksPage] Lazy loading outstanding details via RPC');

      const { data, error } = await supabase.rpc('get_outstanding_details', {
        p_project_id: projectId
      });

      if (error) throw error;

      const { outstanding, site_requirements: siteReqs } = data || {};

      setOutstandingChecklists(outstanding || []);
      setSiteRequirements(siteReqs || []);
      setOutstandingDetailsLoaded(true);

      console.log('[TasksPage] Outstanding details loaded:', {
        outstanding: (outstanding || []).length,
        siteRequirements: (siteReqs || []).length
      });

    } catch (error) {
      console.error('[TasksPage] Error fetching outstanding details:', error);
      toast.error('Failed to load outstanding checklists');
    } finally {
      setLoadingOutstandingChecklists(false);
      setLoadingSiteRequirements(false);
    }
  };

  // Fetch library data (workflows + pages) in a single RPC call
  // Replaces 2 sequential queries with 1 RPC call
  const fetchLibraryData = async () => {
    try {
      setLoadingWorkflows(true);
      setLoadingPages(true);
      console.log('[TasksPage] Fetching library data via RPC for project:', projectId);

      const { data, error } = await supabase.rpc('get_library_data', {
        p_project_id: projectId
      });

      if (error) throw error;

      const { workflows: workflowsData, pages: pagesData } = data || {};

      setWorkflows(workflowsData || []);
      setPages(pagesData || []);

      console.log('[TasksPage] Library data loaded:', {
        workflows: (workflowsData || []).length,
        pages: (pagesData || []).length
      });
    } catch (error) {
      console.error('[TasksPage] Error fetching library data:', error);
      toast.error('Failed to load library');
    } finally {
      setLoadingWorkflows(false);
      setLoadingPages(false);
    }
  };

  // Legacy functions for backwards compatibility (used after create/update/delete)
  const fetchWorkflows = async () => {
    await fetchLibraryData();
  };

  const fetchPages = async () => {
    await fetchLibraryData();
  };

  // Fetch schedules data via optimized RPC
  const fetchSchedules = async () => {
    try {
      setLoadingSchedules(true);
      console.log('[TasksPage] Fetching schedules via RPC for project:', projectId);

      const { data, error } = await supabase.rpc('get_schedules_data', {
        p_project_id: projectId
      });

      if (error) throw error;

      const { schedules: schedulesData } = data || {};
      setSchedules(schedulesData || []);

      console.log('[TasksPage] Schedules loaded:', (schedulesData || []).length);
    } catch (error) {
      console.error('[TasksPage] Error fetching schedules:', error);
      toast.error('Failed to load schedules');
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Fetch company processes available to this space
  const fetchCompanyProcesses = async () => {
    if (!projectId) return;

    try {
      setLoadingCompanyProcesses(true);

      const { data, error } = await supabase.rpc('get_available_processes_for_space', {
        p_project_id: projectId
      });

      if (error) throw error;

      setCompanyProcesses(data || []);
    } catch (error) {
      console.error('Error fetching company processes:', error);
      toast.error('Failed to load company processes');
    } finally {
      setLoadingCompanyProcesses(false);
    }
  };

  // Start a company process in this space
  const handleStartProcess = async (process) => {
    if (!projectId || !process?.id) return;

    try {
      setStartingProcess(process.id);

      const { data, error } = await supabase.rpc('start_process_in_space', {
        p_workflow_id: process.id,
        p_project_id: projectId,
        p_run_name: null // Will auto-generate name
      });

      if (error) throw error;

      toast.success(`Started "${process.name}" - ${data?.[0]?.task_run_count || 0} tasks created`);

      // Refresh workflow runs to show the new run
      fetchWorkflowRuns();
      fetchWorkflowTaskRuns();

      // Switch to Active tab to show the new run
      handleNavigate('tasks');
    } catch (error) {
      console.error('Error starting process:', error);
      toast.error('Failed to start process');
    } finally {
      setStartingProcess(null);
    }
  };

  // Fetch outstanding checklists (internal_checklist requirements without linked task/workflow)
  const fetchOutstandingChecklists = async () => {
    if (!projectId) return;

    try {
      setLoadingOutstandingChecklists(true);

      // Get all internal checklists from site_compliance_requirements
      const { data: checklists, error: checklistError } = await supabase
        .from('site_compliance_requirements')
        .select('*')
        .eq('project_id', projectId)
        .eq('requirement_type', 'internal_checklist')
        .eq('approval_status', 'approved')
        .in('status', ['active', 'pending', 'compliant'])
        .order('created_date', { ascending: false });

      if (checklistError) throw checklistError;

      // Get IDs of requirements that have linked tasks
      const { data: linkedTasks, error: tasksError } = await supabase
        .from('tasks')
        .select('compliance_requirement_id')
        .eq('project_id', projectId)
        .not('compliance_requirement_id', 'is', null);

      if (tasksError) throw tasksError;

      // Get IDs of requirements that have linked workflows
      const { data: linkedWorkflows, error: workflowsError } = await supabase
        .from('workflows')
        .select('compliance_requirement_id')
        .eq('project_id', projectId)
        .not('compliance_requirement_id', 'is', null);

      if (workflowsError) throw workflowsError;

      // Create a set of linked requirement IDs
      const linkedIds = new Set([
        ...(linkedTasks || []).map(t => t.compliance_requirement_id),
        ...(linkedWorkflows || []).map(w => w.compliance_requirement_id)
      ]);

      // Filter to only show checklists WITHOUT linked items
      const outstanding = (checklists || []).filter(c => !linkedIds.has(c.id));

      setOutstandingChecklists(outstanding);
    } catch (error) {
      console.error('Error fetching outstanding checklists:', error);
      toast.error('Failed to load outstanding checklists');
    } finally {
      setLoadingOutstandingChecklists(false);
    }
  };

  // Fetch site requirements (internal_checklist requirements WITH linked task/workflow)
  const fetchSiteRequirements = async () => {
    if (!projectId) return;

    try {
      setLoadingSiteRequirements(true);

      // Get all internal checklists from site_compliance_requirements
      const { data: checklists, error: checklistError } = await supabase
        .from('site_compliance_requirements')
        .select('*')
        .eq('project_id', projectId)
        .eq('requirement_type', 'internal_checklist')
        .eq('approval_status', 'approved')
        .in('status', ['active', 'pending', 'compliant'])
        .order('created_date', { ascending: false });

      if (checklistError) throw checklistError;

      // Get tasks with linked requirements (include task details)
      const { data: linkedTasks, error: tasksError } = await supabase
        .from('tasks')
        .select('id, name, compliance_requirement_id, due_date, priority')
        .eq('project_id', projectId)
        .not('compliance_requirement_id', 'is', null);

      if (tasksError) throw tasksError;

      // Get workflows with linked requirements (include workflow details)
      const { data: linkedWorkflows, error: workflowsError } = await supabase
        .from('workflows')
        .select('id, name, compliance_requirement_id, status')
        .eq('project_id', projectId)
        .not('compliance_requirement_id', 'is', null);

      if (workflowsError) throw workflowsError;

      // Create maps for quick lookup
      const taskMap = new Map();
      (linkedTasks || []).forEach(t => {
        taskMap.set(t.compliance_requirement_id, t);
      });

      const workflowMap = new Map();
      (linkedWorkflows || []).forEach(w => {
        workflowMap.set(w.compliance_requirement_id, w);
      });

      // Filter to only show checklists WITH linked items and attach link info
      const siteReqs = (checklists || [])
        .filter(c => taskMap.has(c.id) || workflowMap.has(c.id))
        .map(c => ({
          ...c,
          linkedTask: taskMap.get(c.id) || null,
          linkedWorkflow: workflowMap.get(c.id) || null
        }));

      setSiteRequirements(siteReqs);
    } catch (error) {
      console.error('Error fetching site requirements:', error);
      toast.error('Failed to load site requirements');
    } finally {
      setLoadingSiteRequirements(false);
    }
  };

  // Fetch workflow runs for Reports view via optimized RPC
  const fetchWorkflowRuns = async () => {
    try {
      setLoadingReports(true);
      console.log('[TasksPage] Fetching reports data via RPC for project:', projectId);

      const { data, error } = await supabase.rpc('get_reports_data', {
        p_project_id: projectId
      });

      if (error) throw error;

      const { workflow_runs: runsData, workflows: workflowsForFilter } = data || {};
      setWorkflowRuns(runsData || []);

      // Also update workflows if not already loaded (for filter dropdown)
      if (workflows.length === 0 && workflowsForFilter?.length > 0) {
        setWorkflows(workflowsForFilter);
      }

      console.log('[TasksPage] Reports data loaded:', (runsData || []).length, 'runs');
    } catch (error) {
      console.error('[TasksPage] Error loading workflow runs:', error);
      toast.error('Failed to load workflow runs');
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchWorkflowTaskRuns = async () => {
    try {
      setLoadingWorkflowTaskRuns(true);
      console.log('Fetching workflow task runs for project:', projectId);

      // Single RPC call replaces 4 sequential queries
      const { data, error } = await supabase.rpc('get_workflow_task_runs_with_context', {
        p_project_id: projectId,
        p_user_id: userProfile?.id || null
      });

      if (error) throw error;

      // Extract data from RPC response
      const {
        workflow_runs: workflowRuns,
        task_runs: taskRuns,
        comment_counts: commentCounts,
        assignee_profiles: assigneeProfiles
      } = data || {};

      if (!workflowRuns || workflowRuns.length === 0) {
        setWorkflowTaskRuns([]);
        return;
      }

      console.log('Workflow task runs fetch result:', {
        runCount: workflowRuns.length,
        taskRunCount: taskRuns?.length
      });

      // Build lookup maps for O(1) access
      const workflowRunMap = new Map();
      const workflowTaskMap = new Map(); // Keyed by "workflow_id:task_id"
      workflowRuns.forEach(run => {
        workflowRunMap.set(run.id, run);
        // Pre-index workflow tasks for O(1) lookup
        const tasks = run.workflows?.tasks || [];
        tasks.forEach(task => {
          workflowTaskMap.set(`${run.workflow_id}:${task.id}`, task);
        });
      });

      // Filter to only tasks assigned to current user
      const myTaskRuns = (taskRuns || []).filter(taskRun => {
        const assignees = taskRun.assignees || [];
        // Check if user is directly assigned (handles both config objects and plain UUID strings)
        const isDirectlyAssigned = assignees.some(a =>
          typeof a === 'string'
            ? a === userProfile?.id
            : a.type === 'user' && (a.id === userProfile?.id || a.user_id === userProfile?.id)
        );
        // Check if workflow_runner is assigned (user who started the workflow)
        const isWorkflowRunner = assignees.some(a => typeof a === 'object' && a.type === 'workflow_runner');
        // For now, show all tasks in active workflow runs if no specific assignees
        // or if the user matches the assignment criteria
        return assignees.length === 0 || isDirectlyAssigned || isWorkflowRunner;
      });

      // Map to unified task format using O(1) lookups
      const mappedTasks = myTaskRuns.map(taskRun => {
        // O(1) workflow run lookup
        const workflowRun = workflowRunMap.get(taskRun.workflow_run_id);
        const workflow = workflowRun?.workflows;

        // O(1) workflow task lookup
        const taskKey = `${workflowRun?.workflow_id}:${taskRun.task_id}`;
        const workflowTask = workflowTaskMap.get(taskKey) ||
                            { name: `Task ${taskRun.task_order + 1}`, formTitle: '' };

        // Map assignees to profile objects (handles both config objects and plain UUID strings)
        const taskAssignees = (taskRun.assignees || [])
          .filter(a => typeof a === 'string' || (typeof a === 'object' && a.type === 'user'))
          .map(a => {
            const userId = typeof a === 'string' ? a : (a.id || a.user_id);
            const profile = assigneeProfiles?.[userId];
            return profile || { id: userId, first_name: 'Unknown', last_name: '', avatar_url: null };
          });

        return {
          id: taskRun.id,
          name: workflowTask.formTitle || workflowTask.name || `Task ${taskRun.task_order + 1}`,
          description: '',
          due_date: taskRun.due_date,
          created_at: taskRun.created_date,
          myCompletion: taskRun.status === 'completed',
          isWorkflowTask: true,
          workflow_run_id: taskRun.workflow_run_id,
          workflow_run_name: workflowRun?.run_name,
          workflow_name: workflow?.name,
          workflow_id: workflowRun?.workflow_id,
          task_order: taskRun.task_order,
          status: taskRun.status,
          started_at: workflowRun?.started_at,
          assignees: taskAssignees,
          assigned_to: taskAssignees.map(a => a.id),
          commentCount: commentCounts?.[taskRun.workflow_run_id] || 0,
        };
      });

      console.log('Workflow task runs:', mappedTasks);
      setWorkflowTaskRuns(mappedTasks);
    } catch (error) {
      console.error('Error fetching workflow task runs:', error);
    } finally {
      setLoadingWorkflowTaskRuns(false);
    }
  };

  const handleCreateTask = async (task) => {
    try {
      const assignees = task.assignedTo && task.assignedTo.length > 0
        ? task.assignedTo
        : [userProfile.id];

      const { data, error } = await supabase
        .from('tasks')
        .insert([
          {
            project_id: projectId,
            name: task.name,
            description: task.description,
            subtasks: task.subtasks,
            attachments: task.attachments || [],
            created_by: userProfile.id,
            assigned_to: assignees,
            due_date: task.dueDate || null,
            priority: task.priority || null,
            compliance_requirement_id: task.compliance_requirement_id || null,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      toast.success('Task created successfully!');
      fetchTasks(); // Refresh tasks list
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleViewTask = (task) => {
    // If it's a workflow task, navigate to the workflow run page
    if (task.isWorkflowTask && task.workflow_run_id) {
      navigate(`/space/${projectId}/workflow-run/${task.workflow_run_id}`);
      return;
    }
    setSelectedTask(task);
    setViewTaskOpen(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setViewTaskOpen(false);
    setEditTaskOpen(true);
  };

  const handleUpdateTask = async (taskId, updatedTask) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          name: updatedTask.name,
          description: updatedTask.description,
          subtasks: updatedTask.subtasks,
          link_to_workflow: updatedTask.linkToWorkflow,
          due_date: updatedTask.dueDate || null,
          priority: updatedTask.priority || null,
        })
        .eq('id', taskId);

      if (error) throw error;

      toast.success('Task updated successfully!');
      fetchTasks(); // Refresh tasks list
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  // Direct navigation to create a new blank workflow (bypasses dialog temporarily)
  const handleCreateNewWorkflow = () => {
    // Navigate to workflow editor, passing linked requirement if present
    const params = new URLSearchParams();
    if (linkedComplianceRequirement?.id) {
      params.set('complianceRequirementId', linkedComplianceRequirement.id);
      params.set('name', linkedComplianceRequirement.name || '');
    }
    const queryString = params.toString();
    navigate(`/space/${projectId}/workflow-editor${queryString ? `?${queryString}` : ''}`);
    setLinkedComplianceRequirement(null);
  };

  // Dialog option handler - kept for future use when dialog is re-enabled
  const handleWorkflowOptionSelect = (optionId) => {
    console.log('Workflow option selected:', optionId);
    setCreateWorkflowOpen(false);

    if (optionId === 'blank') {
      handleCreateNewWorkflow();
    } else {
      // TODO: Handle other workflow creation options
      toast.info(`${optionId} workflow creation coming soon!`);
    }
  };

  const handleOpenWorkflow = (workflowId, status) => {
    // Navigate based on workflow status
    if (status === 'published') {
      // Published workflows go to management page
      navigate(`/space/${projectId}/library/${workflowId}`);
    } else {
      // Draft workflows go to editor
      navigate(`/space/${projectId}/workflow-editor?id=${workflowId}`);
    }
  };

  const handleOpenPage = (pageId) => {
    // Navigate to page editor with the page ID
    navigate(`/space/${projectId}/page-editor?id=${pageId}`);
  };

  const handleDuplicateWorkflow = async (workflow, e) => {
    e.stopPropagation();
    try {
      const { data, error } = await supabase
        .from('workflows')
        .insert({
          project_id: projectId,
          name: `${workflow.name} (Copy)`,
          description: workflow.description,
          tasks: workflow.tasks,
          triggers: workflow.triggers,
          settings: workflow.settings,
          status: 'draft',
          created_by: userProfile.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Workflow duplicated');
      fetchWorkflows();
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      toast.error('Failed to duplicate workflow');
    }
  };

  const handleDeleteWorkflow = async (workflowId, e) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this workflow? This cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('workflows')
        .delete()
        .eq('id', workflowId);

      if (error) throw error;

      toast.success('Workflow deleted');
      setWorkflows(prev => prev.filter(w => w.id !== workflowId));
    } catch (error) {
      console.error('Error deleting workflow:', error);
      toast.error('Failed to delete workflow');
    }
  };

  const handleDuplicatePage = async (page, e) => {
    e.stopPropagation();
    try {
      const { data, error } = await supabase
        .from('pages')
        .insert({
          project_id: projectId,
          name: `${page.name} (Copy)`,
          content_blocks: page.content_blocks,
          settings: page.settings,
          status: 'draft',
          created_by: userProfile.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Page duplicated');
      fetchPages();
    } catch (error) {
      console.error('Error duplicating page:', error);
      toast.error('Failed to duplicate page');
    }
  };

  const handleDeletePage = async (pageId, e) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this page? This cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('pages')
        .delete()
        .eq('id', pageId);

      if (error) throw error;

      toast.success('Page deleted');
      setPages(prev => prev.filter(p => p.id !== pageId));
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handleMoveWorkflow = async (workflowId, newVisibility) => {
    try {
      const { error } = await supabase
        .from('workflows')
        .update({ visibility: newVisibility })
        .eq('id', workflowId);

      if (error) throw error;

      setWorkflows(prev => prev.map(w =>
        w.id === workflowId ? { ...w, visibility: newVisibility } : w
      ));
      toast.success(`Moved to ${newVisibility === 'space' ? space?.name || 'Space' : 'Private Library'}`);
    } catch (error) {
      console.error('Error moving workflow:', error);
      toast.error('Failed to move workflow');
    }
  };

  const handleMovePage = async (pageId, newVisibility) => {
    try {
      const { error } = await supabase
        .from('pages')
        .update({ visibility: newVisibility })
        .eq('id', pageId);

      if (error) throw error;

      setPages(prev => prev.map(p =>
        p.id === pageId ? { ...p, visibility: newVisibility } : p
      ));
      toast.success(`Moved to ${newVisibility === 'space' ? space?.name || 'Space' : 'Private Library'}`);
    } catch (error) {
      console.error('Error moving page:', error);
      toast.error('Failed to move page');
    }
  };

  // Group tasks based on groupBy setting
  const groupedTasks = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: null, tasks: sortedTasks }];
    }

    const groups = {};

    if (groupBy === 'due_date') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);

      sortedTasks.forEach(task => {
        if (!task.due_date) {
          if (!groups['no_due_date']) groups['no_due_date'] = { key: 'no_due_date', label: 'No due date', tasks: [] };
          groups['no_due_date'].tasks.push(task);
        } else {
          const dueDate = new Date(task.due_date);
          dueDate.setHours(0, 0, 0, 0);

          if (dueDate < today) {
            if (!groups['overdue']) groups['overdue'] = { key: 'overdue', label: 'Overdue', tasks: [] };
            groups['overdue'].tasks.push(task);
          } else if (dueDate.getTime() === today.getTime()) {
            if (!groups['due_today']) groups['due_today'] = { key: 'due_today', label: 'Due today', tasks: [] };
            groups['due_today'].tasks.push(task);
          } else if (dueDate < weekEnd) {
            if (!groups['due_this_week']) groups['due_this_week'] = { key: 'due_this_week', label: 'Due this week', tasks: [] };
            groups['due_this_week'].tasks.push(task);
          } else {
            if (!groups['due_later']) groups['due_later'] = { key: 'due_later', label: 'Due later', tasks: [] };
            groups['due_later'].tasks.push(task);
          }
        }
      });

      // Return groups in order
      const orderedKeys = ['overdue', 'due_today', 'due_this_week', 'due_later', 'no_due_date'];
      return orderedKeys.map(key => groups[key]).filter(Boolean);
    } else if (groupBy === 'assignee') {
      sortedTasks.forEach(task => {
        if (!task.assignees || task.assignees.length === 0) {
          if (!groups['unassigned']) groups['unassigned'] = { key: 'unassigned', label: 'Unassigned', tasks: [] };
          groups['unassigned'].tasks.push(task);
        } else {
          task.assignees.forEach(assignee => {
            const key = assignee.id;
            const label = `${assignee.first_name} ${assignee.last_name}`;
            if (!groups[key]) groups[key] = { key, label, tasks: [] };
            groups[key].tasks.push(task);
          });
        }
      });
      return Object.values(groups);
    } else if (groupBy === 'workflow') {
      sortedTasks.forEach(task => {
        const key = task.link_to_workflow ? 'linked' : 'not_linked';
        const label = task.link_to_workflow ? 'Linked to workflow' : 'Not linked';
        if (!groups[key]) groups[key] = { key, label, tasks: [] };
        groups[key].tasks.push(task);
      });
      return Object.values(groups);
    }

    return [{ key: 'all', label: null, tasks: sortedTasks }];
  }, [sortedTasks, groupBy]);

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const handleSortColumn = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with default ascending direction
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Helper to calculate the next due date based on recurrence type
  const calculateNextDueDate = (currentDueDate, recurrenceType) => {
    const baseDate = currentDueDate ? new Date(currentDueDate) : new Date();

    switch (recurrenceType) {
      case 'daily':
        return addDays(baseDate, 1);
      case 'weekly':
        return addWeeks(baseDate, 1);
      case 'biweekly':
        return addWeeks(baseDate, 2);
      case 'monthly':
        return addMonths(baseDate, 1);
      case 'quarterly':
        return addMonths(baseDate, 3);
      case 'yearly':
        return addYears(baseDate, 1);
      default:
        return null;
    }
  };

  // Create the next occurrence of a recurring task
  const createNextOccurrence = async (task) => {
    // Only create next occurrence for recurring tasks
    if (!task.recurrence_type || task.recurrence_type === 'none') {
      return;
    }

    try {
      // Check if a next occurrence already exists (to prevent duplicates)
      const { data: existingNext, error: checkError } = await supabase
        .from('tasks')
        .select('id')
        .eq('parent_task_id', task.id)
        .limit(1);

      if (checkError) throw checkError;

      // If there's already a next occurrence, don't create another
      if (existingNext && existingNext.length > 0) {
        console.log('[TasksPage] Next occurrence already exists for task:', task.id);
        return;
      }

      // Calculate the next due date
      const nextDueDate = calculateNextDueDate(task.due_date, task.recurrence_type);

      // Check recurrence end date
      if (task.recurrence_end_date && nextDueDate > new Date(task.recurrence_end_date)) {
        console.log('[TasksPage] Recurrence end date reached for task:', task.id);
        toast.info('This was the last occurrence of this recurring task');
        return;
      }

      // Create the next occurrence
      const { data: newTask, error: createError } = await supabase
        .from('tasks')
        .insert({
          name: task.name,
          description: task.description,
          project_id: task.project_id,
          created_by: task.created_by,
          assigned_to: task.assigned_to,
          due_date: nextDueDate?.toISOString(),
          priority: task.priority,
          subtasks: task.subtasks?.map(st => ({ ...st, completed: false })), // Reset subtasks
          link_to_workflow: task.link_to_workflow,
          recurrence_type: task.recurrence_type,
          recurrence_interval: task.recurrence_interval,
          recurrence_end_date: task.recurrence_end_date,
          parent_task_id: task.parent_task_id || task.id, // Link to original parent
          recurrence_count: (task.recurrence_count || 0) + 1,
        })
        .select()
        .single();

      if (createError) throw createError;

      console.log('[TasksPage] Created next occurrence:', newTask.id);
      toast.success('Next recurring task created');

      // Refresh the task list to show the new task
      await fetchTasks();
    } catch (error) {
      console.error('[TasksPage] Error creating next occurrence:', error);
      // Don't show error toast - the completion succeeded, just the next occurrence failed
    }
  };

  const handleSelectTask = (taskId, checked) => {
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(taskId);
      } else {
        newSet.delete(taskId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allTaskIds = new Set(sortedTasks.map(t => t.id));
      setSelectedTasks(allTaskIds);
    } else {
      setSelectedTasks(new Set());
    }
  };

  const handleBulkMarkComplete = async () => {
    try {
      const selectedIds = Array.from(selectedTasks);

      // Separate standalone tasks from workflow tasks
      const standaloneTasks = tasks.filter(t => selectedTasks.has(t.id));
      const workflowTasks = workflowTaskRuns.filter(t => selectedTasks.has(t.id));

      let completedCount = 0;

      // Handle standalone tasks - upsert into task_completions
      if (standaloneTasks.length > 0) {
        const standaloneUpdates = standaloneTasks.map(task => ({
          task_id: task.id,
          user_id: userProfile.id,
          completed: true,
          completed_at: new Date().toISOString()
        }));

        const { error: standaloneError } = await supabase
          .from('task_completions')
          .upsert(standaloneUpdates, {
            onConflict: 'task_id,user_id'
          });

        if (standaloneError) throw standaloneError;
        completedCount += standaloneTasks.length;

        // Update local state for standalone tasks
        setTasks(prevTasks =>
          prevTasks.map(t =>
            selectedTasks.has(t.id)
              ? { ...t, myCompletion: true }
              : t
          )
        );

        // Create next occurrences for recurring tasks
        const recurringTasks = standaloneTasks.filter(
          t => t.recurrence_type && t.recurrence_type !== 'none'
        );
        for (const task of recurringTasks) {
          await createNextOccurrence(task);
        }
      }

      // Handle workflow tasks - update checklists_workflow_task_runs status
      if (workflowTasks.length > 0) {
        const workflowTaskIds = workflowTasks.map(t => t.id);

        const { error: workflowError } = await supabase
          .from('checklists_workflow_task_runs')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
            completed_by: userProfile.id
          })
          .in('id', workflowTaskIds);

        if (workflowError) throw workflowError;
        completedCount += workflowTasks.length;

        // Update local state for workflow tasks
        setWorkflowTaskRuns(prevRuns =>
          prevRuns.map(t =>
            selectedTasks.has(t.id)
              ? { ...t, status: 'completed', myCompletion: true }
              : t
          )
        );
      }

      setSelectedTasks(new Set());
      toast.success(`Marked ${completedCount} task(s) as completed`);
    } catch (error) {
      console.error('Error marking tasks complete:', error);
      toast.error('Failed to mark tasks as completed');
    }
  };

  const handleToggleMyCompletion = async (checked, task) => {
    try {
      console.log('[TasksPage] Toggling completion:', {
        taskId: task.id,
        taskName: task.name,
        userId: userProfile.id,
        userName: `${userProfile.first_name} ${userProfile.last_name}`,
        oldStatus: task.myCompletion,
        newStatus: checked
      });

      // Optimistically update local state
      setTasks(prevTasks =>
        prevTasks.map(t =>
          t.id === task.id
            ? { ...t, myCompletion: checked }
            : t
        )
      );

      const { error } = await supabase
        .from('task_completions')
        .upsert({
          task_id: task.id,
          user_id: userProfile.id,
          completed: checked,
          completed_at: checked ? new Date().toISOString() : null
        }, {
          onConflict: 'task_id,user_id'
        });

      if (error) throw error;

      console.log('[TasksPage] Completion toggled successfully:', {
        userId: userProfile.id,
        taskId: task.id,
        newStatus: checked
      });

      // If marking as complete and it's a recurring task, create the next occurrence
      if (checked && task.recurrence_type && task.recurrence_type !== 'none') {
        await createNextOccurrence(task);
      }
    } catch (error) {
      console.error('[TasksPage] Error toggling completion:', error);
      toast.error('Failed to update task');
      // Revert optimistic update on error
      setTasks(prevTasks =>
        prevTasks.map(t =>
          t.id === task.id
            ? { ...t, myCompletion: !checked }
            : t
        )
      );
    }
  };

  if (loading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3 pageTitle='' pageDescription=''>
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Space'}
          spaceImage={space?.logo_url}
          toolName={
            currentView === 'tasks' ? 'Tasks' :
            currentView === 'live-runs' ? 'Live Runs' :
            currentView === 'schedules' ? 'Schedules' :
            currentView === 'outstanding' ? 'Outstanding' :
            currentView === 'library' ? (
              librarySubView === 'company' ? 'Company Processes' :
              librarySubView === 'personal' ? 'Personal Library' :
              `${space?.name || 'Space'} Library`
            ) : 'Tasks'
          }
          currentTool='tasks'
          spaceTools={spaceTools}
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setCreateTaskOpen(true)}>
                  <CheckSquare2 className="h-4 w-4 mr-2" />
                  Task
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleCreateNewWorkflow}>
                  <GitBranch className="h-4 w-4 mr-2" />
                  Process
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(projectId ? `/space/${projectId}/page-editor` : '/tasks/page-editor')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Page
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(projectId ? `/space/${projectId}/schedule-editor` : '/tasks/schedule-editor')}>
                  <Clock className="h-4 w-4 mr-2" />
                  Schedule
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />

        {/* Main layout with sidebar - fixed height accounting for navbar (56px) + ToolHeader (57px) */}
        <div className="flex h-[calc(100vh-113px)]">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0 h-full overflow-hidden">
            <TasksSidebar
              projectId={projectId}
              space={space}
              currentView={currentView}
              librarySubView={librarySubView}
              onNavigate={handleNavigate}
              outstandingCount={outstandingCount}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 h-full overflow-y-auto">
            {currentView === 'tasks' ? (
              <div className="container mx-auto px-4 py-6 space-y-4">
                {/* Stats Header Bar */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-900/30 rounded-xl border border-slate-200/50 dark:border-slate-700/50">
                  <div className="flex items-center divide-x divide-slate-200 dark:divide-slate-700">
                    <div className="pr-6 flex items-center gap-2">
                      <CheckSquare2 className="w-4 h-4 text-teal-600" />
                      <span className="text-lg font-semibold">{taskStats.pendingTasks}</span>
                      <span className="text-xs text-muted-foreground">pending</span>
                    </div>

                    <div className="px-6 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      <span className="text-lg font-semibold">{taskStats.completedTasks}</span>
                      <span className="text-xs text-muted-foreground">completed</span>
                    </div>

                    {taskStats.overdueTasks > 0 && (
                      <div className="px-6 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-lg font-semibold text-red-600">{taskStats.overdueTasks}</span>
                        <span className="text-xs text-muted-foreground">overdue</span>
                      </div>
                    )}

                    <div className="px-6 flex items-center gap-2">
                      <GitBranch className="w-4 h-4 text-blue-500" />
                      <span className="text-lg font-semibold">{taskStats.workflowTasks}</span>
                      <span className="text-xs text-muted-foreground">from workflows</span>
                    </div>

                    <div className="pl-6 flex items-center gap-2">
                      <Users className="w-4 h-4 text-pink-500" />
                      <span className="text-lg font-semibold">{taskStats.activeAssignees}</span>
                      <span className="text-xs text-muted-foreground">assignees</span>
                    </div>
                  </div>

                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                    <Button
                      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => setViewMode('list')}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => setViewMode('calendar')}
                    >
                      <CalendarDays className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Filters and Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                  {/* Filter Toolbar */}
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Search */}
                      <div className="relative flex-1 min-w-[200px] max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search tasks..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9 pr-9 h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />

                      {/* Type Filter */}
                      <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="w-[130px] h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                          <SelectValue placeholder="All types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All types</SelectItem>
                          <SelectItem value="workflows">
                            <div className="flex items-center gap-2">
                              <GitBranch className="w-3.5 h-3.5 text-blue-500" />
                              Workflows
                            </div>
                          </SelectItem>
                          <SelectItem value="tasks">
                            <div className="flex items-center gap-2">
                              <CheckSquare2 className="w-3.5 h-3.5 text-teal-500" />
                              Tasks
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Assignee Filter */}
                      <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                        <SelectTrigger className="w-[160px] h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={userProfile?.avatar_url} />
                              <AvatarFallback className="text-xs">{userProfile?.first_name?.[0] || 'U'}</AvatarFallback>
                            </Avatar>
                            <span className="truncate">
                              {filterAssignee === 'me' ? 'Assigned to me' : filterAssignee === 'anyone' ? 'Anyone' : 'Unassigned'}
                            </span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="me">Assigned to me</SelectItem>
                          <SelectItem value="anyone">Assigned to anyone</SelectItem>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Workflow Filter */}
                      <Select value={filterWorkflow} onValueChange={setFilterWorkflow}>
                        <SelectTrigger className="w-[160px] h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                          <SelectValue placeholder="All workflows" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All workflows</SelectItem>
                          {uniqueWorkflows.map(workflow => (
                            <SelectItem key={workflow.id} value={workflow.id}>
                              {workflow.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Group by */}
                      <Select value={groupBy} onValueChange={setGroupBy}>
                        <SelectTrigger className="w-[140px] h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2">
                            <Grid3X3 className="h-3.5 w-3.5" />
                            <span>Group by</span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="workflow">Workflow</SelectItem>
                          <SelectItem value="assignee">Assignee</SelectItem>
                          <SelectItem value="due_date">Due date</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Show Completed Toggle */}
                      <div className="flex items-center gap-2 px-2">
                        <Switch
                          id="show-completed"
                          checked={showCompletedTasks}
                          onCheckedChange={setShowCompletedTasks}
                          className="scale-90"
                        />
                        <label htmlFor="show-completed" className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
                          Show completed
                        </label>
                      </div>

                      {/* Results count */}
                      <div className="text-sm text-muted-foreground ml-auto">
                        {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Tasks List View */}
                  {viewMode === 'list' && (
                    loadingTasks || loadingWorkflowTaskRuns ? (
                      <div className="flex items-center justify-center py-16">
                        <LoadingSpinner />
                      </div>
                    ) : sortedTasks.length === 0 ? (
                      <div className="py-16 text-center">
                        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                          <CheckSquare2 className="w-6 h-6 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">No tasks yet</h3>
                        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                          Create your first task to start tracking your work.
                        </p>
                        <Button onClick={() => setCreateTaskOpen(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Create Task
                        </Button>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-50/50">
                            <TableHead className="w-12">
                              <Checkbox
                                checked={selectedTasks.size > 0 && selectedTasks.size === sortedTasks.length}
                                onCheckedChange={handleSelectAll}
                              />
                            </TableHead>
                            <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500" style={{ width: '25%' }}>
                              <button
                                onClick={() => handleSortColumn('name')}
                                className="flex items-center gap-2 hover:text-primary transition-colors"
                              >
                                Name
                                {sortColumn === 'name' ? (
                                  sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </TableHead>
                            <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500" style={{ width: '8%' }}>
                              <button
                                onClick={() => handleSortColumn('due_date')}
                                className="flex items-center gap-2 hover:text-primary transition-colors"
                              >
                                Due
                                {sortColumn === 'due_date' ? (
                                  sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </TableHead>
                            <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500" style={{ width: '10%' }}>
                              <button
                                onClick={() => handleSortColumn('started_at')}
                                className="flex items-center gap-1 hover:text-primary transition-colors whitespace-nowrap"
                              >
                                <Clock className="h-3.5 w-3.5" />
                                Run Date
                                {sortColumn === 'started_at' ? (
                                  sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </TableHead>
                            <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500" style={{ width: '14%' }}>
                              <button
                                onClick={() => handleSortColumn('workflow_run_name')}
                                className="flex items-center gap-2 hover:text-primary transition-colors"
                              >
                                <CheckSquare2 className="h-4 w-4" />
                                Workflow Run
                                {sortColumn === 'workflow_run_name' ? (
                                  sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </TableHead>
                            <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500" style={{ width: '14%' }}>
                              <button
                                onClick={() => handleSortColumn('workflow_name')}
                                className="flex items-center gap-2 hover:text-primary transition-colors"
                              >
                                <Grid3X3 className="h-4 w-4" />
                                Workflow
                                {sortColumn === 'workflow_name' ? (
                                  sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </TableHead>
                            <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500" style={{ width: '12%' }}>
                              <button
                                onClick={() => handleSortColumn('assignees')}
                                className="flex items-center gap-2 hover:text-primary transition-colors"
                              >
                                Assignees
                                {sortColumn === 'assignees' ? (
                                  sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </TableHead>
                            <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500" style={{ width: '8%' }}>
                              <button
                                onClick={() => handleSortColumn('commentCount')}
                                className="flex items-center gap-1 hover:text-primary transition-colors"
                              >
                                <MessageSquare className="h-4 w-4" />
                                {sortColumn === 'commentCount' ? (
                                  sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupedTasks.map((group) => (
                            <React.Fragment key={group.key}>
                              {/* Group Header */}
                              {group.label && (
                                <TableRow key={`group-${group.key}`} className="bg-slate-50/70 dark:bg-slate-800/50 hover:bg-slate-50/70">
                                  <TableCell colSpan={8} className="p-2">
                                    <button
                                      onClick={() => toggleGroupCollapse(group.key)}
                                      className="flex items-center gap-2 w-full text-left hover:bg-slate-100 dark:hover:bg-slate-700 rounded px-2 py-1 transition-colors"
                                    >
                                      {collapsedGroups.has(group.key) ? (
                                        <ChevronRight className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )}
                                      <span className="font-semibold text-sm">
                                        {group.label} ({group.tasks.length})
                                      </span>
                                    </button>
                                  </TableCell>
                                </TableRow>
                              )}
                              {/* Group Tasks */}
                              {!collapsedGroups.has(group.key) && group.tasks.map((task) => {
                                const assignees = task.assignees || [];
                                const dueDate = task.due_date
                                  ? new Date(task.due_date).toLocaleDateString()
                                  : '-';
                                const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !task.myCompletion;

                                return (
                                  <TableRow
                                    key={task.id}
                                    className="cursor-pointer group hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors border-b border-slate-100 dark:border-slate-800"
                                    onClick={() => handleViewTask(task)}
                                  >
                                    <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        checked={selectedTasks.has(task.id)}
                                        onCheckedChange={(checked) => handleSelectTask(task.id, checked)}
                                        disabled={task.myCompletion}
                                      />
                                    </TableCell>
                                    <TableCell className="py-3">
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <div className={cn(
                                          "w-8 h-8 rounded-lg flex items-center justify-center shadow-sm",
                                          task.myCompletion ? "bg-gradient-to-br from-emerald-500 to-emerald-600" :
                                          task.isWorkflowTask ? "bg-gradient-to-br from-blue-500 to-purple-600" :
                                          "bg-gradient-to-br from-teal-500 to-teal-600"
                                        )}>
                                          {task.isWorkflowTask ? (
                                            <GitBranch className="h-4 w-4 text-white" />
                                          ) : (
                                            <CheckSquare2 className="h-4 w-4 text-white" />
                                          )}
                                        </div>
                                        <span className={cn(
                                          "font-medium truncate text-sm",
                                          task.myCompletion && "line-through text-muted-foreground"
                                        )}>
                                          {task.name}
                                        </span>
                                        {task.priority && PRIORITY_CONFIG[task.priority] && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <div className={cn("flex-shrink-0", PRIORITY_CONFIG[task.priority].color)}>
                                                  {(() => {
                                                    const PriorityIcon = PRIORITY_CONFIG[task.priority].icon;
                                                    return <PriorityIcon className="h-4 w-4" />;
                                                  })()}
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>{PRIORITY_CONFIG[task.priority].label} priority</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-3">
                                      {task.due_date ? (
                                        <div className={cn(
                                          "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium",
                                          isOverdue
                                            ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                            : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                                        )}>
                                          <Calendar className="w-3 h-3" />
                                          {dueDate}
                                          {isOverdue && <span className="font-bold">!</span>}
                                        </div>
                                      ) : (
                                        <span className="text-muted-foreground text-sm">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-3 text-muted-foreground text-sm">
                                      {task.started_at
                                        ? new Date(task.started_at).toLocaleDateString()
                                        : '-'}
                                    </TableCell>
                                    <TableCell className="py-3 text-sm truncate">
                                      {task.isWorkflowTask && task.workflow_run_name && (
                                        <span className="text-primary hover:underline cursor-pointer">
                                          {task.workflow_run_name}
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-3 text-sm truncate">
                                      {task.isWorkflowTask && task.workflow_name ? (
                                        <span className="text-blue-600">
                                          {task.workflow_name}
                                        </span>
                                      ) : task.link_to_workflow && (
                                        <span className="text-primary hover:underline cursor-pointer">
                                          Linked
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-3">
                                      <TooltipProvider>
                                        <div className="flex -space-x-2">
                                          {assignees.slice(0, 3).map((assignee) => (
                                            <Tooltip key={assignee.id}>
                                              <TooltipTrigger asChild>
                                                <Avatar className="h-7 w-7 border-2 border-white dark:border-slate-800 shadow-sm cursor-default">
                                                  <AvatarImage src={assignee.avatar_url} />
                                                  <AvatarFallback className="text-xs bg-slate-100 dark:bg-slate-700">
                                                    {assignee.first_name?.[0] || 'U'}
                                                  </AvatarFallback>
                                                </Avatar>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>{assignee.first_name} {assignee.last_name}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          ))}
                                          {assignees.length > 3 && (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <div className="h-7 w-7 rounded-full border-2 border-white dark:border-slate-800 bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-medium cursor-default shadow-sm">
                                                  +{assignees.length - 3}
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <div className="space-y-1">
                                                  {assignees.slice(3).map((assignee) => (
                                                    <p key={assignee.id}>{assignee.first_name} {assignee.last_name}</p>
                                                  ))}
                                                </div>
                                              </TooltipContent>
                                            </Tooltip>
                                          )}
                                        </div>
                                      </TooltipProvider>
                                    </TableCell>
                                    <TableCell className="py-3 text-sm">
                                      <div className={cn(
                                        "flex items-center gap-1",
                                        task.commentCount > 0 ? "text-foreground" : "text-muted-foreground/40"
                                      )}>
                                        <MessageSquare className="h-4 w-4" />
                                        {task.commentCount || 0}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </React.Fragment>
                          ))}
                          {/* New row */}
                          <TableRow
                            className="hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                            onClick={() => setCreateTaskOpen(true)}
                          >
                            <TableCell colSpan={8} className="p-3 text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Plus className="h-4 w-4" />
                                New task
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    )
                  )}

                  {/* Calendar View */}
                  {viewMode === 'calendar' && (
                    <div className="p-4">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newDate = new Date(calendarMonth);
                      newDate.setMonth(newDate.getMonth() - 1);
                      setCalendarMonth(newDate);
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h2 className="text-lg font-semibold min-w-[180px] text-center">
                    {format(calendarMonth, 'MMMM yyyy')}
                  </h2>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newDate = new Date(calendarMonth);
                      newDate.setMonth(newDate.getMonth() + 1);
                      setCalendarMonth(newDate);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCalendarMonth(new Date())}
                >
                  Today
                </Button>
              </div>

              {/* Calendar Grid */}
              {(() => {
                // Get first day of month and last day of month
                const firstDayOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
                const lastDayOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);

                // Get starting day of week (0 = Sunday)
                const startingDayOfWeek = firstDayOfMonth.getDay();

                // Get number of days in month
                const daysInMonth = lastDayOfMonth.getDate();

                // Build calendar days array
                const calendarDays = [];

                // Add empty cells for days before first of month
                for (let i = 0; i < startingDayOfWeek; i++) {
                  calendarDays.push(null);
                }

                // Add days of month
                for (let day = 1; day <= daysInMonth; day++) {
                  calendarDays.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
                }

                // Group tasks by date
                const tasksByDate = {};
                sortedTasks.forEach(task => {
                  if (task.due_date) {
                    const dateKey = format(new Date(task.due_date), 'yyyy-MM-dd');
                    if (!tasksByDate[dateKey]) {
                      tasksByDate[dateKey] = [];
                    }
                    tasksByDate[dateKey].push(task);
                  }
                });

                const today = format(new Date(), 'yyyy-MM-dd');

                return (
                  <div>
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar cells */}
                    <div className="grid grid-cols-7 gap-1">
                      {calendarDays.map((date, index) => {
                        if (!date) {
                          return <div key={`empty-${index}`} className="min-h-[120px] bg-muted/30 rounded-lg" />;
                        }

                        const dateKey = format(date, 'yyyy-MM-dd');
                        const dayTasks = tasksByDate[dateKey] || [];
                        const isToday = dateKey === today;
                        const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

                        return (
                          <div
                            key={dateKey}
                            className={`min-h-[120px] border rounded-lg p-2 ${
                              isToday ? 'border-primary bg-primary/5' :
                              isPast ? 'bg-muted/20' : 'bg-card'
                            }`}
                          >
                            <div className={`text-sm font-medium mb-1 ${
                              isToday ? 'text-primary' :
                              isPast ? 'text-muted-foreground' : ''
                            }`}>
                              {date.getDate()}
                            </div>
                            <div className="space-y-1 overflow-y-auto max-h-[85px]">
                              {dayTasks.slice(0, 3).map(task => {
                                const priorityConfig = task.priority ? PRIORITY_CONFIG[task.priority] : null;
                                return (
                                  <button
                                    key={task.id}
                                    onClick={() => {
                                      setSelectedTask(task);
                                      setViewTaskOpen(true);
                                    }}
                                    className={`w-full text-left text-xs p-1.5 rounded truncate ${
                                      task.isWorkflowTask
                                        ? 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                                        : task.isCompleted
                                        ? 'bg-green-100 hover:bg-green-200 text-green-800 line-through'
                                        : 'bg-muted hover:bg-muted/80'
                                    }`}
                                  >
                                    <div className="flex items-center gap-1">
                                      {priorityConfig && (
                                        <priorityConfig.icon className={`h-3 w-3 flex-shrink-0 ${priorityConfig.color}`} />
                                      )}
                                      <span className="truncate">{task.name}</span>
                                    </div>
                                  </button>
                                );
                              })}
                              {dayTasks.length > 3 && (
                                <div className="text-xs text-muted-foreground text-center">
                                  +{dayTasks.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Tasks without due dates */}
              {(() => {
                const tasksWithoutDueDate = sortedTasks.filter(task => !task.due_date);
                if (tasksWithoutDueDate.length === 0) return null;

                return (
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">
                      No due date ({tasksWithoutDueDate.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {tasksWithoutDueDate.slice(0, 10).map(task => {
                        const priorityConfig = task.priority ? PRIORITY_CONFIG[task.priority] : null;
                        return (
                          <button
                            key={task.id}
                            onClick={() => {
                              setSelectedTask(task);
                              setViewTaskOpen(true);
                            }}
                            className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                              task.isWorkflowTask
                                ? 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                                : task.isCompleted
                                ? 'bg-green-100 hover:bg-green-200 text-green-800 line-through'
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {priorityConfig && (
                              <priorityConfig.icon className={`h-3 w-3 ${priorityConfig.color}`} />
                            )}
                            {task.name}
                          </button>
                        );
                      })}
                      {tasksWithoutDueDate.length > 10 && (
                        <span className="text-xs text-muted-foreground self-center">
                          +{tasksWithoutDueDate.length - 10} more
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
                    </div>
                  )}
                </div>
              </div>
            ) : currentView === 'schedules' ? (
              <div className="container mx-auto px-4 py-6 space-y-4">
                {/* Stats Header Bar */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-900/30 rounded-xl border border-slate-200/50 dark:border-slate-700/50">
                  <div className="flex items-center divide-x divide-slate-200 dark:divide-slate-700">
                    <div className="pr-6 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-violet-600" />
                      <span className="text-lg font-semibold">{schedules.filter(s => s.is_active).length}</span>
                      <span className="text-xs text-muted-foreground">active</span>
                    </div>

                    <div className="px-6 flex items-center gap-2">
                      <Circle className="w-4 h-4 text-slate-400" />
                      <span className="text-lg font-semibold">{schedules.filter(s => !s.is_active).length}</span>
                      <span className="text-xs text-muted-foreground">paused</span>
                    </div>

                    <div className="px-6 flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-blue-500" />
                      <span className="text-lg font-semibold">{schedules.length}</span>
                      <span className="text-xs text-muted-foreground">total schedules</span>
                    </div>

                    <div className="pl-6 flex items-center gap-2">
                      <GitBranch className="w-4 h-4 text-purple-500" />
                      <span className="text-lg font-semibold">{new Set(schedules.map(s => s.workflow_id)).size}</span>
                      <span className="text-xs text-muted-foreground">workflows</span>
                    </div>
                  </div>

                  {/* Create button */}
                  <Button onClick={() => navigate(projectId ? `/space/${projectId}/schedule-editor` : '/tasks/schedule-editor')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Schedule
                  </Button>
                </div>

                {/* Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                  {loadingSchedules ? (
                    <div className="flex items-center justify-center py-16">
                      <LoadingSpinner />
                    </div>
                  ) : schedules.length === 0 ? (
                    <div className="py-16 text-center">
                      <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                        <Clock className="w-6 h-6 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">No schedules yet</h3>
                      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                        Automate your workflows by running them on a schedule.
                      </p>
                      <Button onClick={() => navigate(projectId ? `/space/${projectId}/schedule-editor` : '/tasks/schedule-editor')}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Schedule
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-50/50">
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Workflow</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Frequency</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Next Run</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {schedules.map((schedule) => (
                          <TableRow
                            key={schedule.id}
                            className="cursor-pointer group hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors border-b border-slate-100 dark:border-slate-800"
                            onClick={() => navigate(projectId
                              ? `/space/${projectId}/schedule-summary?id=${schedule.id}`
                              : `/tasks/schedule-summary?id=${schedule.id}`
                            )}
                          >
                            <TableCell className="py-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                                  <Clock className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-medium text-sm">{schedule.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="flex items-center gap-2">
                                <GitBranch className="h-4 w-4 text-muted-foreground" />
                                <span className="truncate max-w-[150px] text-sm">{schedule.workflow?.name || 'Unknown'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                <span className="capitalize">{schedule.frequency}</span>
                                {schedule.frequency === 'weekly' && schedule.days_of_week && (
                                  <span className="text-muted-foreground">
                                    ({schedule.days_of_week.map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]).join(', ')})
                                  </span>
                                )}
                                <span className="text-muted-foreground">at {schedule.time?.slice(0, 5)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              {schedule.next_run_at ? (
                                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(schedule.next_run_at).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell className="py-3">
                              <Badge variant={schedule.is_active ? 'default' : 'secondary'} className={cn(
                                schedule.is_active && "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400"
                              )}>
                                {schedule.is_active ? 'Active' : 'Paused'}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(projectId
                                      ? `/space/${projectId}/schedule-editor?id=${schedule.id}`
                                      : `/tasks/schedule-editor?id=${schedule.id}`
                                    );
                                  }}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (confirm('Are you sure you want to delete this schedule?')) {
                                        try {
                                          await supabase
                                            .from('workflow_schedules')
                                            .delete()
                                            .eq('id', schedule.id);
                                          setSchedules(prev => prev.filter(s => s.id !== schedule.id));
                                          toast.success('Schedule deleted');
                                        } catch (err) {
                                          console.error('Error deleting schedule:', err);
                                          toast.error('Failed to delete schedule');
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            ) : currentView === 'outstanding' ? (
              <div className="container mx-auto px-4 py-6 space-y-4">
                {/* Stats Header Bar */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-900/30 rounded-xl border border-slate-200/50 dark:border-slate-700/50">
                  <div className="flex items-center divide-x divide-slate-200 dark:divide-slate-700">
                    <div className="pr-6 flex items-center gap-2">
                      {outstandingChecklists.length > 0 ? (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      )}
                      <span className="text-lg font-semibold">{outstandingChecklists.length}</span>
                      <span className="text-xs text-muted-foreground">outstanding</span>
                    </div>

                    <div className="px-6 flex items-center gap-2">
                      <Flag className="w-4 h-4 text-red-500" />
                      <span className="text-lg font-semibold">{outstandingChecklists.filter(c => c.priority === 'high').length}</span>
                      <span className="text-xs text-muted-foreground">high priority</span>
                    </div>

                    <div className="pl-6 flex items-center gap-2">
                      <ListChecks className="w-4 h-4 text-blue-500" />
                      <span className="text-lg font-semibold">{outstandingChecklists.length}</span>
                      <span className="text-xs text-muted-foreground">checklists pending</span>
                    </div>
                  </div>
                </div>

                {/* Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                  {loadingOutstandingChecklists ? (
                    <div className="flex items-center justify-center py-16">
                      <LoadingSpinner />
                    </div>
                  ) : outstandingChecklists.length === 0 ? (
                    <div className="py-16 text-center">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
                      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                        There are no outstanding checklists to complete.
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-50/50">
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Priority</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Frequency</TableHead>
                          <TableHead className="w-[120px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {outstandingChecklists.map((checklist) => (
                          <TableRow
                            key={checklist.id}
                            className="group hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors border-b border-slate-100 dark:border-slate-800"
                          >
                            <TableCell className="py-3">
                              <div className="flex items-center gap-2.5">
                                <div className={cn(
                                  "w-8 h-8 rounded-lg flex items-center justify-center shadow-sm",
                                  checklist.priority === 'high'
                                    ? "bg-gradient-to-br from-red-500 to-orange-600"
                                    : "bg-gradient-to-br from-amber-500 to-yellow-600"
                                )}>
                                  <ListChecks className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-medium text-sm">{checklist.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-3 text-muted-foreground max-w-[200px] truncate text-sm">
                              {checklist.description || '-'}
                            </TableCell>
                            <TableCell className="py-3">
                              <Badge variant={checklist.priority === 'high' ? 'destructive' : 'secondary'} className={cn(
                                checklist.priority === 'high' && "bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400"
                              )}>
                                {checklist.priority || 'Normal'}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 capitalize">
                                {checklist.inspection_frequency?.replace(/_/g, ' ') || '-'}
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="sm" className="h-8">
                                    <Plus className="h-4 w-4 mr-1" />
                                    Create
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setLinkedComplianceRequirement(checklist);
                                    setCreateTaskOpen(true);
                                  }}>
                                    <CheckSquare className="h-4 w-4 mr-2" />
                                    Create Task
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setLinkedComplianceRequirement(checklist);
                                    setCreateWorkflowOpen(true);
                                  }}>
                                    <GitBranch className="h-4 w-4 mr-2" />
                                    Create Workflow
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            ) : currentView === 'library' ? (
              <div className="p-6">
                {librarySubView === 'company' ? (
                  // Company Processes View - Table format
                  <div>
                    {loadingCompanyProcesses ? (
                      <div className="flex items-center justify-center py-12">
                        <LoadingSpinner />
                      </div>
                    ) : companyProcesses.length === 0 ? (
                      <div className="text-center py-12">
                        <GitBranch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">Company Processes</h3>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                          No processes are available for this space. Contact your administrator to share company processes with this space.
                        </p>
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden bg-card">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead>Tasks</TableHead>
                              <TableHead>Scope</TableHead>
                              <TableHead className="w-[100px]">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {companyProcesses.map((process) => {
                              const taskCount = process.tasks?.length || 0;
                              return (
                                <TableRow key={process.id}>
                                  <TableCell className="font-medium">
                                    {process.name}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground max-w-[300px] truncate">
                                    {process.description || '-'}
                                  </TableCell>
                                  <TableCell>{taskCount}</TableCell>
                                  <TableCell>
                                    {process.is_global ? (
                                      <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                                        <Globe className="h-3 w-3" />
                                        Global
                                      </Badge>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      onClick={() => handleStartProcess(process)}
                                      disabled={startingProcess === process.id}
                                      size="sm"
                                    >
                                      {startingProcess === process.id ? (
                                        <>
                                          <LoadingSpinner className="h-4 w-4 mr-2" />
                                          Starting...
                                        </>
                                      ) : (
                                        <>
                                          <Play className="h-4 w-4 mr-2" />
                                          Run
                                        </>
                                      )}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ) : (() => {
                  // Filter pages and workflows based on selected library sub-view
                  const filteredPages = pages.filter(p => {
                    const visibility = p.visibility || 'private';
                    if (librarySubView === 'personal') {
                      return visibility === 'private' && p.created_by === userProfile?.id;
                    }
                    return visibility === 'space';
                  });

                  const filteredWorkflows = workflows.filter(w => {
                    const visibility = w.visibility || 'private';
                    if (librarySubView === 'personal') {
                      return visibility === 'private' && w.created_by === userProfile?.id;
                    }
                    return visibility === 'space';
                  });

                  // Combine pages and workflows into a single list for the table
                  const combinedItems = [
                    ...filteredPages.map(p => ({ ...p, itemType: 'page' })),
                    ...filteredWorkflows.map(w => ({ ...w, itemType: 'workflow' }))
                  ].sort((a, b) => new Date(b.updated_date || b.created_date) - new Date(a.updated_date || a.created_date));

                  if (loadingWorkflows || loadingPages) {
                    return (
                      <div className="flex items-center justify-center py-12">
                        <LoadingSpinner />
                      </div>
                    );
                  }

                  if (combinedItems.length === 0) {
                    return (
                      <div className="text-center py-12">
                        {librarySubView === 'personal' ? (
                          <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        ) : (
                          <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        )}
                        <p className="text-muted-foreground mb-4">
                          No items in {librarySubView === 'personal' ? 'Personal Library' : space?.name || 'Space'}
                        </p>
                        <div className="flex items-center gap-2 justify-center">
                          <Button onClick={handleCreateNewWorkflow}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create workflow
                          </Button>
                          <Button variant="outline" onClick={() => navigate(projectId ? `/space/${projectId}/page-editor` : '/tasks/page-editor')}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create page
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="border rounded-lg overflow-hidden bg-card">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead className="w-[80px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {combinedItems.map((item) => {
                            const isPage = item.itemType === 'page';
                            const itemCount = isPage
                              ? (item.content_blocks?.length || 0)
                              : (item.tasks?.length || 0);
                            const itemLabel = isPage ? 'blocks' : 'tasks';

                            const getStatusBadge = (status) => {
                              switch (status) {
                                case 'draft':
                                  return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Draft</Badge>;
                                case 'published':
                                case 'active':
                                  return <Badge variant="outline" className="text-green-600 border-green-600">Published</Badge>;
                                case 'archived':
                                  return <Badge variant="secondary">Archived</Badge>;
                                default:
                                  return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
                              }
                            };

                            return (
                              <TableRow
                                key={`${item.itemType}-${item.id}`}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => isPage ? handleOpenPage(item.id) : handleOpenWorkflow(item.id, item.status)}
                              >
                                <TableCell className="font-medium">
                                  <span className="truncate max-w-[200px]">{item.name}</span>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary">
                                    {isPage ? 'Page' : 'Workflow'}
                                  </Badge>
                                </TableCell>
                                <TableCell>{getStatusBadge(item.status)}</TableCell>
                                <TableCell className="text-muted-foreground">
                                  {new Date(item.updated_date || item.created_date).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {itemCount} {itemLabel}
                                </TableCell>
                                <TableCell>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        isPage ? handleOpenPage(item.id) : handleOpenWorkflow(item.id, item.status);
                                      }}>
                                        <Pencil className="h-4 w-4 mr-2" />
                                        Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        isPage ? handleDuplicatePage(item, e) : handleDuplicateWorkflow(item, e);
                                      }}>
                                        <Copy className="h-4 w-4 mr-2" />
                                        Duplicate
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        const targetVisibility = librarySubView === 'personal' ? 'space' : 'private';
                                        isPage ? handleMovePage(item.id, targetVisibility) : handleMoveWorkflow(item.id, targetVisibility);
                                      }}>
                                        <FolderInput className="h-4 w-4 mr-2" />
                                        Move to {librarySubView === 'personal' ? space?.name || 'Space' : 'Personal Library'}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          isPage ? handleDeletePage(item.id, e) : handleDeleteWorkflow(item.id, e);
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  );
                })()}
              </div>
            ) : currentView === 'live-runs' ? (
              <div className="p-6 space-y-5">
                {/* Stats Header Bar - Compact metrics strip */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-900/50 dark:to-slate-800/30 rounded-xl border border-slate-200/60 dark:border-slate-700/40 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center divide-x divide-slate-200 dark:divide-slate-700">
                      <div className="pr-6 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-lg font-semibold">{reportStats.activeRuns}</span>
                        <span className="text-xs text-muted-foreground">active</span>
                      </div>

                      <div className="px-6 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="text-lg font-semibold">{reportStats.completedRuns}</span>
                        <span className="text-xs text-muted-foreground">completed</span>
                      </div>

                      <div className="px-6 flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-orange-500" />
                        <span className="text-lg font-semibold">{reportStats.totalRuns}</span>
                        <span className="text-xs text-muted-foreground">total runs</span>
                      </div>

                      <div className="px-6 flex items-center gap-2">
                        <ListChecks className="w-4 h-4 text-indigo-500" />
                        <span className="text-lg font-semibold">{reportStats.totalTasks}</span>
                        <span className="text-xs text-muted-foreground">tasks</span>
                      </div>

                      <div className="pl-6 flex items-center gap-2">
                        <Users className="w-4 h-4 text-pink-500" />
                        <span className="text-lg font-semibold">{reportStats.activeAssignees}</span>
                        <span className="text-xs text-muted-foreground">assignees</span>
                      </div>
                    </div>

                    {/* Workflows badge */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                      <BarChart3 className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-semibold">{reportStats.totalWorkflows}</span>
                      <span className="text-xs text-muted-foreground">workflows</span>
                    </div>
                  </div>
                </div>

                {/* Filters and Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                  {/* Filter Toolbar */}
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Search */}
                      <div className="relative flex-1 min-w-[200px] max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search runs, workflows, tasks..."
                          value={runSearchQuery}
                          onChange={(e) => setRunSearchQuery(e.target.value)}
                          className="pl-9 pr-9 h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                        />
                        {runSearchQuery && (
                          <button
                            onClick={() => setRunSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />

                      {/* Status Filter */}
                      <Select value={selectedRunStatus} onValueChange={setSelectedRunStatus}>
                        <SelectTrigger className="w-[140px] h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                              Active
                            </div>
                          </SelectItem>
                          <SelectItem value="all">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-slate-400" />
                              All Statuses
                            </div>
                          </SelectItem>
                          <SelectItem value="in_progress">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              In Progress
                            </div>
                          </SelectItem>
                          <SelectItem value="completed">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              Completed
                            </div>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-slate-400" />
                              Cancelled
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Workflow Filter */}
                      <Select value={selectedReportWorkflow} onValueChange={setSelectedReportWorkflow}>
                        <SelectTrigger className="w-[180px] h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                          <SelectValue placeholder="All Workflows" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Workflows</SelectItem>
                          {workflows.map((workflow) => (
                            <SelectItem key={workflow.id} value={workflow.id}>
                              {workflow.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Date Range Filter */}
                      <Popover open={dateFilterOpen} onOpenChange={(open) => {
                        setDateFilterOpen(open);
                        if (open) setTempDateRange(runDateRange);
                      }}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "h-9 justify-start text-left font-normal bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700",
                              !runDateRange.from && !runDateRange.to && "text-muted-foreground",
                              (runDateRange.from || runDateRange.to) && "border-blue-300 bg-blue-50 dark:bg-blue-900/20"
                            )}
                          >
                            <CalendarRange className="mr-2 h-4 w-4" />
                            {runDateRange.from ? (
                              runDateRange.to ? (
                                <>{format(runDateRange.from, 'MMM d')} - {format(runDateRange.to, 'MMM d')}</>
                              ) : format(runDateRange.from, 'MMM d, yyyy')
                            ) : "Date range"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarPicker
                            mode="range"
                            selected={tempDateRange}
                            onSelect={setTempDateRange}
                            numberOfMonths={2}
                          />
                          <div className="flex items-center justify-between p-3 border-t">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setTempDateRange({ from: null, to: null });
                              setRunDateRange({ from: null, to: null });
                              setDateFilterOpen(false);
                            }}>
                              Clear
                            </Button>
                            <Button size="sm" onClick={() => {
                              setRunDateRange(tempDateRange);
                              setDateFilterOpen(false);
                            }}>
                              Apply
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>

                      {/* Results count */}
                      <div className="text-sm text-muted-foreground ml-auto">
                        {filteredRuns.length} run{filteredRuns.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Table Content */}
                  {loadingReports ? (
                    <div className="flex items-center justify-center py-16">
                      <LoadingSpinner />
                    </div>
                  ) : workflowRuns.length === 0 ? (
                    <div className="py-16 text-center">
                      <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                        <GitBranch className="w-6 h-6 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">No workflow runs yet</h3>
                      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                        Start a workflow to begin tracking progress.
                      </p>
                      <Button onClick={() => handleNavigate('library', 'space')}>
                        <Play className="w-4 h-4 mr-2" />
                        Go to Workflows
                      </Button>
                    </div>
                  ) : filteredRuns.length === 0 ? (
                    <div className="py-16 text-center">
                      <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                        <Search className="w-6 h-6 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">No runs match your filters</h3>
                      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                        Try adjusting your search or filter criteria.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSelectedRunStatus('all');
                          setSelectedReportWorkflow('all');
                          setRunDateRange({ from: null, to: null });
                          setRunSearchQuery('');
                        }}
                      >
                        Clear all filters
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-50/50">
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Workflow</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Run</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Started By</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Started</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Due</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 w-[180px]">Progress</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRuns.map((run) => {
                          const progress = getRunProgress(run);
                          const dueDate = getRunDueDate(run);
                          const isOverdue = isDueDateOverdue(dueDate);

                          return (
                            <TableRow
                              key={run.id}
                              className="cursor-pointer group hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors border-b border-slate-100 dark:border-slate-800"
                              onClick={() => navigate(`/space/${projectId}/workflow-run/${run.id}`)}
                            >
                              <TableCell className="py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm">
                                    <GitBranch className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-medium text-sm truncate max-w-[140px]">
                                    {run.workflow?.name || 'Unknown'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3">
                                <span className="font-medium text-foreground">
                                  {run.run_name || 'Untitled Run'}
                                </span>
                              </TableCell>
                              <TableCell className="py-3">
                                {run.trigger_type === 'webhook' || run.trigger_type === 'schedule' || (!run.started_by && !run.started_by_user) ? (
                                  <div className="flex items-center gap-2">
                                    <div className="h-7 w-7 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                      {run.trigger_type === 'schedule' ? (
                                        <Clock className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
                                      ) : (
                                        <Webhook className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
                                      )}
                                    </div>
                                    <span className="text-sm text-muted-foreground">{run.trigger_type === 'schedule' ? 'Scheduled' : 'Webhook'}</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-7 w-7 border-2 border-white dark:border-slate-800 shadow-sm">
                                      <AvatarImage src={run.started_by_user?.avatar_url} />
                                      <AvatarFallback className="text-xs bg-slate-100 dark:bg-slate-700">
                                        {run.started_by_user?.first_name?.charAt(0) || '?'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm">{run.started_by_user ? `${run.started_by_user.first_name || ''}`.trim() : 'Unknown'}</span>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="py-3 text-sm text-muted-foreground">
                                {run.started_at
                                  ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
                                  : '-'
                                }
                              </TableCell>
                              <TableCell className="py-3">
                                {dueDate ? (
                                  <div className={cn(
                                    "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium",
                                    isOverdue
                                      ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                      : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                                  )}>
                                    <Calendar className="w-3 h-3" />
                                    {format(dueDate, 'MMM d')}
                                    {isOverdue && <span className="font-bold">!</span>}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-sm">—</span>
                                )}
                              </TableCell>
                              <TableCell className="py-3">
                                {run.status === 'in_progress' ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                    In Progress
                                  </span>
                                ) : run.status === 'completed' ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Completed
                                  </span>
                                ) : run.status === 'cancelled' ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                    <XCircle className="w-3 h-3" />
                                    Cancelled
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                    {run.status}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="py-3">
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div
                                      className={cn(
                                        "h-full rounded-full transition-all duration-500",
                                        progress.percent === 100
                                          ? "bg-emerald-500"
                                          : "bg-blue-500"
                                      )}
                                      style={{ width: `${progress.percent}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-muted-foreground tabular-nums w-12">
                                    {progress.completed}/{progress.total}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/space/${projectId}/workflow-run/${run.id}`);
                                    }}>
                                      <Eye className="w-4 h-4 mr-2" />
                                      View Run
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/space/${projectId}/library/${run.workflow_id}`);
                                    }}>
                                      <GitBranch className="w-4 h-4 mr-2" />
                                      View Workflow
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteRunId(run.id);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete Run
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Create Task Sheet */}
        <CreateTaskSheet
          open={createTaskOpen}
          onOpenChange={setCreateTaskOpen}
          onCreateTask={handleCreateTask}
          linkedComplianceRequirement={linkedComplianceRequirement}
          onClearLinkedRequirement={() => setLinkedComplianceRequirement(null)}
        />

        {/* View Task Sheet */}
        <ViewTaskSheet
          open={viewTaskOpen}
          onOpenChange={setViewTaskOpen}
          task={selectedTask}
          onTaskUpdate={fetchTasks}
          onEditTask={handleEditTask}
        />

        {/* Edit Task Sheet */}
        <EditTaskSheet
          open={editTaskOpen}
          onOpenChange={setEditTaskOpen}
          task={selectedTask}
          onUpdateTask={handleUpdateTask}
        />

        {/* Create Workflow Modal */}
        <CreateWorkflowModal
          open={createWorkflowOpen}
          onOpenChange={setCreateWorkflowOpen}
          onSelectOption={handleWorkflowOptionSelect}
          linkedComplianceRequirement={linkedComplianceRequirement}
          onClearLinkedRequirement={() => setLinkedComplianceRequirement(null)}
        />

        {/* Floating Action Bar */}
        {selectedTasks.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
            <div className="bg-gray-900 text-white rounded-full shadow-2xl px-6 py-3 flex items-center gap-4">
              <span className="text-sm font-medium">
                {selectedTasks.size} selected
              </span>
              <div className="h-6 w-px bg-gray-700" />
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-gray-800 gap-2"
                onClick={handleBulkMarkComplete}
              >
                <CheckSquare2 className="h-4 w-4" />
                Mark as completed
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-gray-800 gap-2"
                onClick={() => setSelectedTasks(new Set())}
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            </div>
          </div>
        )}
        {/* Delete Run Dialog */}
        <AlertDialog open={!!deleteRunId} onOpenChange={(open) => !open && setDeleteRunId(null)}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Delete Run</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to delete this run? This action cannot be undone.
                </AlertDialogDescription>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  onClick={async () => {
                    try {
                      await supabase
                        .from('checklists_workflow_runs')
                        .delete()
                        .eq('id', deleteRunId);
                      setWorkflowRuns(prev => prev.filter(r => r.id !== deleteRunId));
                      toast.success('Run deleted');
                    } catch (err) {
                      console.error('Error deleting run:', err);
                      toast.error('Failed to delete run');
                    }
                    setDeleteRunId(null);
                  }}
                >
                  Delete Run
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </PageTransition>
    </AppShell3>
  );
}
